import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import styles from './marketing.module.css';
import { SEO } from '../components/SEO.jsx';
import { BSB_B010 } from './bsb_b010.jsx';
import { TrustIt } from './trust_it.jsx';
import { BSB300 } from './bsb_300.jsx';
import { BSB700 } from './bsb_700.jsx';

import heroImage from '../pictures/Products.avif';
import trustImage from '../pictures/Sample.avif';
import serverImage from '../pictures/PCI_express_front.avif';
import businessImage from '../pictures/BSB 300.avif';
import processorImage from '../pictures/003.avif';
import shopImage from '../pictures/Luxurious goods.webp';

const productCards = [
  {
    tag: 'Web service',
    title: 'Trust.it',
    text: 'A public-facing proof service for people and small businesses who want lasting records without running their own infrastructure.',
    image: trustImage,
    detail: 1,
  },
  {
    tag: 'Infrastructure',
    title: 'Back-Step Server Board',
    text: 'Hardware for service providers and companies that want to operate their own Back-Step Blockchain environment.',
    image: serverImage,
    detail: 2,
  },
  {
    tag: 'Business platform',
    title: 'BSB Business Version',
    text: 'A commercial version for companies building custom proof, ownership, or transaction services for their customers.',
    image: businessImage,
    detail: 3,
  },
  {
    tag: 'High throughput',
    title: 'AI Currency Processor',
    text: 'A prototype for demanding transaction environments where speed, trusted records, and automation meet.',
    image: processorImage,
    detail: 4,
  },
];

const Products = () => {
  const [selectedForm, setSelectedForm] = useState(0);
  const location = useLocation();
  const prevLocationState = useRef(location.state);

  useEffect(() => {
    if (prevLocationState.current !== location.state) {
      setSelectedForm(0);
      prevLocationState.current = location.state;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [location.state]);

  switch (selectedForm) {
    case 1:
      return <TrustIt />;
    case 2:
      return <BSB_B010 />;
    case 3:
      return <BSB300 />;
    case 4:
      return <BSB700 />;
    default:
      break;
  }

  return (
    <main className={styles.page}>
      <SEO
        title="Back-Step Blockchain Products"
        description="Back-Step Blockchain products include Trust.it proof services, blockchain generator hardware, business platforms, and licensing for trusted records."
        path="/products"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'OfferCatalog',
          name: 'Back-Step Blockchain Products',
          itemListElement: productCards.map((product) => ({
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Product',
              name: product.title,
              description: product.text,
              brand: 'Back-Step Blockchain',
            },
          })),
        }}
      />

      <section className={styles.hero}>
        <img className={styles.heroImage} src={heroImage} alt="Back-Step Blockchain product family" />
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <p className={styles.kicker}>Products</p>
          <h1 className={styles.heroTitle}>Choose the level of trust infrastructure you need.</h1>
          <p className={styles.heroText}>
            Start with a ready-to-use proof service, operate your own blockchain server, or license the
            technology for deeper product integration.
          </p>
          <div className={styles.actions}>
            <Link className={styles.primaryAction} to="/contact">Discuss your use case</Link>
            <Link className={styles.secondaryAction} to="/overview">Understand the platform</Link>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <p className={styles.kicker}>Portfolio</p>
            <h2>Products for users, businesses, and infrastructure partners.</h2>
            <p>
              The portfolio is designed to meet customers where they are: a simple web service, dedicated hardware,
              or a platform for new applications.
            </p>
          </div>

          <div className={styles.gridTwo}>
            {productCards.map((product) => (
              <article className={styles.card} key={product.title}>
                <img className={styles.cardImage} src={product.image} alt="" />
                <div className={styles.cardBody}>
                  <span className={styles.tag}>{product.tag}</span>
                  <h3>{product.title}</h3>
                  <p>{product.text}</p>
                  <button className={styles.cardButton} type="button" onClick={() => setSelectedForm(product.detail)}>
                    View product
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.sectionAlt}>
        <div className={styles.split}>
          <div className={styles.splitCopy}>
            <p className={styles.kicker}>Customer promise</p>
            <h2>Make trust part of the product experience.</h2>
            <p>
              For premium products, official records, business documents, and digital value, proof should be
              something customers can understand before they need a technical explanation.
            </p>
            <ul className={styles.list}>
              <li>Proof customers can verify</li>
              <li>Infrastructure partners can operate</li>
              <li>Technology companies can license and integrate</li>
            </ul>
          </div>
          <div className={styles.photoPanel}>
            <img src={shopImage} alt="Luxury retail setting for authenticity and proof use cases" />
          </div>
        </div>
      </section>
    </main>
  );
};

export default Products;
